import * as React from 'react'
import fetch from 'node-fetch'
import { FC, useState, useRef, useCallback, useEffect } from 'react'
import { ProductJsonLd } from 'next-seo'

import Icon from 'components/Icon'
import Heading from 'components/Heading'
import Card from 'components/cards/base/Card'
import StarRating from 'components/StarRating'
import { Constants, Utils } from 'common/utils'
import ReviewsSummary from 'components/ReviewsSummary'
import { ReviewsAPIResponse, TReviews } from 'common/cms-types'
import { reviewsStrings } from 'common/strings/reviews-strings'
import { getPlanPrice } from 'common/utils/planPrice'
import { PlanSlug } from 'common/planSlugs'
import planNames from '../../common/planNames'
import { Tenant, tenantToSlug } from 'common/tenant'

type ReviewsSectionType = {
  data: TReviews
  plan?: PlanSlug
  pageUrl?: string
  includeJsonLd?: boolean
  tenant?: Tenant
}

type ReviewCardProps = {
  name: string
  rating: number
  content: string
}

const ReviewCard: FC<ReviewCardProps> = ({ name, rating, content }) => (
  <Card theme='white' className='rounded-box review-card p-5 p-lg-10' padding='md' shadow>
    <Heading className='text-semibold lead-large mb-0' size={3}>
      {name}
    </Heading>
    <StarRating width={18} height={17} value={rating} />
    <div className='d-flex align-items-center mt-2'>
      <Icon name='tick' className='me-2' />
      <span className='text-semibold text-small'>{reviewsStrings.verifiedBuyer}</span>
    </div>
    <div
      className='mt-2 text-medium review-content text-medium--lt1'
      dangerouslySetInnerHTML={{ __html: content }}
    />
  </Card>
)

const ReviewsSection: FC<ReviewsSectionType> = ({ data, plan = PlanSlug.SCAN_LITE, pageUrl, includeJsonLd = true, tenant = Tenant.UK }) => {
  const au = tenant === Tenant.AU
  const [reviews, setReviews] = useState(data.reviews)
  const [isLoading, setIsLoading] = useState(false)
  const [url, setUrl] = useState(data.url)
  const planPrice = getPlanPrice(tenantToSlug(tenant))
  const currency = au ? 'AUD' : 'GBP'
  const countryCode = au ? 'AU' : 'GB'

  const hasMore = reviews.length < data.numberOfReviews
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    return () => el.removeEventListener('scroll', updateScrollState)
  }, [updateScrollState])

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: direction === 'left' ? -400 : 400, behavior: 'smooth' })
  }

  const getMore = () => {
    if (isLoading) {
      return
    }
    const [_url, _params] = url.split('?')
    const params = Utils.fromParam(_params)
    params.page = `${parseInt(params.page) + 1}`
    setIsLoading(true)
    const newUrl = `${_url}?${Utils.toParam(params)}`
    setUrl(newUrl)
    fetch(newUrl)
      .then((res) => res.json())
      .then((res: ReviewsAPIResponse) => {
        setReviews(reviews.concat(Utils.parseReviews(res.reviews)))
        setIsLoading(false)
      })
  }
  const location = au ? 'Sydney' : 'London'
  const country = au ? 'Australian' : 'UK'

  let description = `Prime virtual office in ${location} from ${planPrice.monthlyOnYearlyPlan(
    PlanSlug.SCAN_LITE,
  )} (${planPrice.monthly[plan]} monthly). Same-day setup, no postage fees, and full Registered & Trading Address support for ${country} businesses.`

  switch (plan) {
    case PlanSlug.SCAN_PRO:
      description = `Mail forwarding add-on from ${planPrice.monthlyOnYearlyPlan(
        PlanSlug.SCAN_PRO,
      )} per month. Receive post to your ${location} business address and have it securely forwarded — same-day dispatch with no hidden postage fees.`
      break;
  }
  return (
    <div style={{ maxWidth: '100vw' }}>
      <Card padding='0' theme={'grey6'} className='reviews-section'>
        <div className='container px-xl-12'>
          <Heading size={2} className='text-semibold text-center pt-4'>
            {reviewsStrings.whatPeopleAreSaying}
          </Heading>
          <ReviewsSummary
            textAlign={'center'}
            average={data.average}
            totalReviews={data.numberOfReviews}
          />
        </div>
        <div className='position-relative'>
          {canScrollLeft && (
            <button
              aria-label='Scroll left'
              className='reviews-scroll-btn reviews-scroll-btn--left'
              onClick={() => scrollBy('left')}
            >
              <Icon name='chevron-left' />
            </button>
          )}
          <div ref={scrollRef} className='overflow-x-auto px-8 custom-scroll py-6'>
            <div className='d-flex flex-nowrap gap-4 gap-lg-8'>
              {reviews.map((review, index) => (
                <ReviewCard
                  key={`${review.name}-${review.date}-${index}`}
                  name={review.name}
                  rating={review.rating}
                  content={review.content}
                />
              ))}
            </div>
          </div>
          {canScrollRight && (
            <button
              aria-label='Scroll right'
              className='reviews-scroll-btn reviews-scroll-btn--right'
              onClick={() => scrollBy('right')}
            >
              <Icon name='chevron-right' />
            </button>
          )}
        </div>
      </Card>
      {includeJsonLd && <ProductJsonLd
        brand={Constants.companyInformation.name}
        images={[
          `${process.env.SITE_URL}/images/hm-product-sm.png`,
          `${process.env.SITE_URL}/images/hm-product-md.png`,
          `${process.env.SITE_URL}/images/hm-product-lg.png`,
        ]}
        offers={
          {
            price: planPrice.monthlyOnYearlyPlanFloat(plan),
            priceCurrency: currency,
            priceValidUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
            availability: "https://schema.org/InStock",
            url: pageUrl ? `${process.env.SITE_URL}${pageUrl}${pageUrl.endsWith('/') ? '' : '/'}` : `${process.env.SITE_URL}`,
            itemCondition: "https://schema.org/NewCondition",
            seller: {
              name: Constants.companyInformation.name
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: "0.00",
                currency: currency
              },
              shippingDestination: [
                {
                  "@type": "DefinedRegion",
                  addressCountry: "GB"
                },
                {
                  "@type": "DefinedRegion",
                  addressCountry: "US"
                },
                {
                  "@type": "DefinedRegion",
                  addressCountry: "AU"
                },
                {
                  "@type": "DefinedRegion",
                  addressCountry: "CA"
                },
                {
                  "@type": "DefinedRegion",
                  addressCountry: "EU"
                }
              ],
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: {
                  "@type": "QuantitativeValue",
                  minValue: 0,
                  maxValue: 0,
                  unitCode: "DAY"
                },
                transitTime: {
                  "@type": "QuantitativeValue",
                  minValue: 0,
                  maxValue: 0,
                  unitCode: "DAY"
                }
              }
            },
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              name: "Hoxton Mix Cancellation Policy",
              applicableCountry: countryCode,
              returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: 7,
              returnMethod: "https://schema.org/ReturnByMail",
              returnFees: "https://schema.org/FreeReturn",
              returnPolicySeasonalOverride: "false",
              additionalProperty: [
                {
                  "@type": "PropertyValue",
                  name: "Cancellation Terms",
                  value: "Subscriptions can be cancelled anytime. Monthly plans: no refunds for partial periods. Annual plans: full refund available if cancelled within 7 days of invoice."
                }
              ]
            },
          }
        }
        productName={au ? 'Hoxton Mix Virtual Office' : planNames[plan]}
        description={description}
        category="Virtual Office Services"
        sku={plan}
        aggregateRating={{
          bestRating: data.best,
          ratingCount: data.numberOfReviews,
          ratingValue: data.average,
          reviewCount: data.numberOfReviews,
          worstRating: data.worst,
        }}
        reviews={reviews.slice(0, 5).map((v) => ({
          author: v.name,
          reviewBody: v.content,
          reviewRating: {
            ratingValue: `${v.rating}`,
          },
        }))}
      />}
    </div>
  )
}

export default ReviewsSection
